import { AxiosResponse } from 'axios';
import { useEffect, useState } from 'react';
import { FieldValues } from 'react-hook-form';

interface RelatedEntityDataProps<TSchema extends FieldValues> {
  data: TSchema[];
  relatedEntityName: string;
  getEntityById: (id: string) => Promise<AxiosResponse<TSchema, TSchema>>;
}

export const useGetRelatedEntityData = <TSchema extends FieldValues>({
  data,
  relatedEntityName,
  getEntityById,
}: RelatedEntityDataProps<TSchema>) => {
  const [entities, setEntities] = useState<Record<number, TSchema | null>>({});
  const [isLoading, setIsLoading] = useState(false);

  useEffect(() => {
    try {
      setIsLoading(true);
      const fetchEntities = async () => {
        const entityPromises = data.map(async (item) => {
          const response = await getEntityById(item[relatedEntityName]);
          return { entityId: item[relatedEntityName], entity: response?.data };
        });

        const resolvedEntities = await Promise.all(entityPromises);
        const entityMap = resolvedEntities.reduce(
          (acc, { entityId, entity }) => {
            acc[entityId] = entity;
            return acc;
          },
          {} as Record<number, TSchema | null>,
        );

        setEntities(entityMap);
      };

      fetchEntities();
    } catch {
      /* empty */
    } finally {
      setIsLoading(false);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [data]);

  return {
    entities,
    relatedEntitiesLoading: isLoading,
  };
};
